import React, { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useMutation, useQueryClient } from 'react-query';
import { useParams } from 'react-router-dom';
import { ronGetOrderQueryKey } from 'hooks/useRONGetOrder';
import querys from 'services/querys';
import { NewOrderType } from 'types/order';
import useAlert from 'utils/alert';
import {
  PARTY_KIND_BUYER,
  PARTY_KIND_ORDER_OPENER,
  PARTY_KIND_SELLER,
  PARTY_KIND_SETTLEMENT_AGENCY,
} from 'utils/constants';

import { tableRowComponent } from '../../services';
import { PartyItem, scheduleMettingTokens } from './services';
import {
  buildPartyItemsFromEmails,
  convertToPartyItemArray,
  orderPartiesAlphabetically,
  uniquePartiesById,
} from './utils';

const defaultKinds = [
  PARTY_KIND_SELLER,
  PARTY_KIND_BUYER,
  PARTY_KIND_ORDER_OPENER,
  PARTY_KIND_SETTLEMENT_AGENCY,
];

const useRonScheduleMeeting = (
  tokensInfo: scheduleMettingTokens,
  onClose: () => void,
  setTokensRon: React.Dispatch<React.SetStateAction<tableRowComponent[]>>,
  graphOrder: NewOrderType | undefined,
  tokenParties: string[]
) => {
  const { t } = useTranslation();
  const { id: orderId } = useParams<{ id: string }>();
  const methods = useForm<scheduleMettingTokens>();
  const showAlert = useAlert();

  const [orderParties, setOrderParties] = useState<PartyItem[]>([]);
  const [currentAttendees, setCurrentAttendees] = useState<PartyItem[]>([]);

  const queryClient = useQueryClient();

  const { mutate: updpateTokens, ...updateTokensResponse } = useMutation(querys.updateOrderTokens, {
    onSuccess: (data) => {
      const { tokens, parties } = data.updateOrderTokens;

      setTokensRon(
        tokens.map((token) => {
          return {
            ...token,
            party_info: parties.find((party) => party._id === token.party_id),
          };
        })
      );

      onClose();
      showAlert(t('ron:meeting-updated'), 'success');
    },
    onError: (e: any) => {
      // The server saves the new tokens before it sends the invitations, so a failed send can leave
      // the list on screen stale. Refetching the order resyncs it.
      queryClient.invalidateQueries(ronGetOrderQueryKey(orderId!));
      const { errors: errorMessages } = e.response;
      showAlert(errorMessages.map((error) => error.message).join(''), "error");
    },
  });

  useEffect(() => {
    const { date, init_time, end_time, id } = tokensInfo;
    const { setValue } = methods;

    if (tokenParties.length === 0) {
      const defaultParties =
        graphOrder?.parties.filter((party) =>
          defaultKinds.some((kind) => party.kinds.includes(kind))
        ) || [];
      setCurrentAttendees(uniquePartiesById(convertToPartyItemArray(defaultParties)));
    } else {
      setCurrentAttendees(uniquePartiesById(buildPartyItemsFromEmails(graphOrder?.parties ?? [], tokenParties)));
    }

    setValue('date', date || '');
    setValue('end_time', end_time || null);
    setValue('init_time', init_time || null);
    setValue('id', id || undefined);
  }, [graphOrder?.parties, methods, tokensInfo, tokenParties]);

  useEffect(() => {
    let partyList: PartyItem[] = [];
    graphOrder?.parties.forEach((party) => {
      partyList = orderPartiesAlphabetically(
        partyList.concat(
          party.emails.map((email) => ({
            _id: email._id,
            partyId: party._id,
            partyName: party.name,
            email: email.address,
            emailKind: email.kind,
            partyKinds: party.kinds,
          }))
        )
      );
    });

    setOrderParties(uniquePartiesById(partyList));
  }, [graphOrder?.parties]);

  return {
    t,
    methods,
    orderId,
    updpateTokens,
    loading: updateTokensResponse.isLoading,
    orderParties,
    currentAttendees,
  };
};

export default useRonScheduleMeeting;
